import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import DynamicTextArea from 'components/common/dynamic-text-area-component';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import { ReactComponent as ChatBotSubmitButton } from 'icons/chat-bot-chat-submit-button.svg';
import Tooltip from 'components/common/tool-tip-component';
import classNames from 'classnames';
import ChatBotPromptSelector from './chatbot-prompt-selector-component';

export const CHATBOT_PANEL_BLOCK = 'chatbot-panel';
export const CHATBOT_PANEL_BLOCK_ID = 'chatbot-panel-id';
export const CHAT_SUBMIT_BUTTON_SIZE = '30px';
const MAX_LENGTH = 500;
const MAX_VISIBLE_ROWS = 3;

// mock data for chatbot prompts
// export const SAMPLE_PROMPTS = [
//   {
//     promptId: 'prompt-1',
//     title: 'chatbot.prompt-library.prompt-1.title',
//     tooltip: 'chatbot.prompt-library.prompt-1.tooltip',
//   },
//   {
//     promptId: 'prompt-2',
//     title: 'chatbot.prompt-library.prompt-2.title',
//     tooltip: 'chatbot.prompt-library.prompt-2.tooltip',
//   },
//   {
//     promptId: 'prompt-3',
//     title: 'chatbot.prompt-library.prompt-3.title',
//     tooltip: 'chatbot.prompt-library.prompt-3.tooltip',
//   },
// ];

export const ChatBotPanelFooter = (
  {
    text,
    show,
    onTextAreaChange,
    showSecondaryMessage,
    setShowSecondaryMessage,
    handleSend,
    messages,
    inputFieldDisable,
    footerRef,
    prompts = [],
    onPromptSelect,
  },
  ref,
) => {
  const disabled =
    text.trim() === '' ||
    (text.length < 3 && text.trim() !== '') ||
    inputFieldDisable;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return; // Allow new line on Shift + Enter
      }
      event.preventDefault(); // Prevent new line on Enter key

      if (!event.shiftKey && !disabled) {
        handleSend();  // Call the send handler when Enter is pressed without Shift and input is not disabled
      }
    }
  };

  return (
    <div
      className={`${CHATBOT_PANEL_BLOCK}__chat-footer-section`}
      ref={footerRef}
    >
      {showSecondaryMessage && (
        <div>
          <div>
            <div className={`${CHATBOT_PANEL_BLOCK}__chat-input`}>
              <div className={`${CHATBOT_PANEL_BLOCK}__text-area`}>
                <DynamicTextArea
                  id={'chat-bot-text-area'}
                  ref={ref}
                  value={text}
                  placeholder={'chatbot-input.placeholder'}
                  focusOnMount={show}
                  maxLength={MAX_LENGTH}
                  onChange={onTextAreaChange}
                  maxVisibleRows={MAX_VISIBLE_ROWS}
                  disabled={inputFieldDisable}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <Tooltip
                id={`${CHATBOT_PANEL_BLOCK_ID}__button-tooltip`}
                text="chatbot.send.button"
                key={`${CHATBOT_PANEL_BLOCK_ID}__button-tooltip`}
                position={'top'}
              >
                <div className={`${CHATBOT_PANEL_BLOCK}__submit-btn`}>
                  <ChatBotSubmitButton
                    width={CHAT_SUBMIT_BUTTON_SIZE}
                    height={CHAT_SUBMIT_BUTTON_SIZE}
                    className={classNames(
                      `${CHATBOT_PANEL_BLOCK}__chat-submit-btn`,
                      {
                        [`${CHATBOT_PANEL_BLOCK}__chat-submit-btn--disabled`]:
                          disabled,
                      },
                    )}
                    onClick={() => !disabled && handleSend()}
                  />
                </div>
              </Tooltip>
            </div>
            <div className={`${CHATBOT_PANEL_BLOCK}__word-count`}>
              {text.length}/{MAX_LENGTH}
            </div>
          </div>
          <div className={`${CHATBOT_PANEL_BLOCK}__model-selector-wrapper`}>
            <ChatBotPromptSelector
              prompts={prompts}
              onPromptSelect={onPromptSelect}
              disabled={inputFieldDisable}
              className={`${CHATBOT_PANEL_BLOCK}__model-selector`}
            />
          </div>
        </div>
      )}
      {!(showSecondaryMessage || messages.length) && (
        <div>
          <Button
            id={`${CHATBOT_PANEL_BLOCK}__i-understand-btn`}
            type={BUTTON_TYPES.primary}
            onClick={() => setShowSecondaryMessage(true)}
            className={`${CHATBOT_PANEL_BLOCK}__btn`}
          >
            <FormattedMessage id={'chatbot.understand.button'} />
          </Button>
        </div>
      )}
    </div>
  );
};

ChatBotPanelFooter.propTypes = {
  // text entered in text area component
  text: PropTypes.string,
  // boolean value to show guidance messages
  showSecondaryMessage: PropTypes.bool.isRequired,
  // action to switch b/w guidance messages
  setShowSecondaryMessage: PropTypes.func.isRequired,
  // array of available prompts
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      tooltip: PropTypes.string,
    }),
  ),
  // function called when a prompt is selected
  onPromptSelect: PropTypes.func,
};

export default forwardRef(ChatBotPanelFooter);
